import { Fragment, useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type UseAstrologerChat, type ChatTarget, type ChatErrorCode } from '../../hooks/useAstrologerChat.js';
import { StarGauge } from '../../components/chat/StarGauge.js';
import { ReportMarkdown } from '../../components/ReportMarkdown.js';
import { closePartialBold } from '../../lib/chat-partial-markdown.js';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OrbitalLoader } from '../../components/brand/OrbitalLoader.js';
import { PrimaryButton, SecondaryButton } from '../../components/chrome/Button.js';
import headerStyles from '../../components/chrome/AppHeader.module.css';
import { chipKeys } from '../../lib/chat-chips.js';
import { isFirstHistoryEntry } from '../../lib/history-back.js';
import { relationDisplayKey } from '../../lib/relation.js';
import { newChatHref, historyThreadHref } from '../../lib/chat-links.js';
import { ChatTopUpSheet } from './ChatTopUpSheet.js';
import { ChatHeaderActions } from './ChatHeaderActions.js';
import { ChatHistorySheet } from './ChatHistorySheet.js';
import { ChatPersonaPicker } from './ChatPersonaPicker.js';
import styles from './AstrologerChat.module.css';

import type { ThreadItem } from '../../hooks/useChatThreads';
/** Same cap as the server (CHAT_MAX_MESSAGE_CHARS in astro-bot api/routes/chat.ts). */
const MAX_CHARS = 2000;
/** Closer than this to the bottom counts as «reading the latest message». */
const NEAR_BOTTOM_PX = 80;

function isTouchDevice(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

interface ErrorView { key: string; retry: boolean; toCharts: boolean }

function errorView(code: ChatErrorCode): ErrorView {
  switch (code) {
    case 'insufficient_stars':
      return { key: 'chat.error.noStars', retry: true, toCharts: false };
    case 'rate':
    case 'in_flight':
      return { key: 'chat.error.rate', retry: true, toCharts: false };
    case 'message_too_long':
      return { key: 'chat.error.tooLong', retry: false, toCharts: false };
    case 'chart_not_ready':
      return { key: 'chat.error.notReady', retry: true, toCharts: false };
    case 'chart_gone':
    case 'chart_not_found':
    case 'not_found':
      return { key: 'chat.error.gone', retry: false, toCharts: true };
    default:
      return { key: 'chat.error.generic', retry: true, toCharts: false };
  }
}

interface CourseView {
  title: string;
  notice: React.ReactNode;
  disabled: boolean;
  loadCode: string | null;
  quickReplies?: string[];
  draftStorageKey?: string;
  priceStars?: number;
}

function readDraft(key: string | undefined): string {
  try { return key ? sessionStorage.getItem(key) ?? '' : ''; }
  catch { return ''; }
}

export function ChatConversationView({ chat, target, course }: { chat: UseAstrologerChat; target: ChatTarget; course?: CourseView }): JSX.Element {
  const { t } = useTranslation(['home', 'me', 'course']);
  const navigate = useNavigate();
  const { messages, streaming, needTopUp, waitingForStars, error, loadState, loadError, header, canShowChips } = chat;
  const { send, retry, reload, dismissTopUp, reloadAfterTopUp } = chat;
  const draftKey = course?.draftStorageKey;
  const [input, setInputState] = useState(() => readDraft(draftKey));
  const setInput = (value: string | ((current: string) => string)): void => {
    const text = typeof value === 'function' ? value(input) : value;
    setInputState(text);
    try {
      if (draftKey) {
        if (text) sessionStorage.setItem(draftKey, text);
        else sessionStorage.removeItem(draftKey);
      }
    } catch { /* In-memory draft still works if storage is unavailable. */ }
  };
  useEffect(() => { setInputState(readDraft(draftKey)); }, [draftKey]);
  useEffect(() => {
    if (!course || !['quote_changed', 'answer_changed', 'version_conflict'].includes(error ?? '')) return;
    const pending = messages.filter((m) => m.role === 'user' && m.unsent).at(-1);
    if (pending && !input.trim()) setInput(pending.content);
  }, [error]);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPersona, setShowPersona] = useState(false);
  const historyButton = useRef<HTMLButtonElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const nearBottom = useRef(true);
  const forceScroll = useRef(false);
  const err = error ? errorView(error) : null;
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!course || (!error && loadState !== 'error')) return;
    errorRef.current?.focus();
    const title = document.title;
    document.title = `${t('course:chat.error')} — ${title}`;
    return () => { document.title = title; };
  }, [error, loadState, !!course, t]);
  const inputOff = loadState !== 'ready' || !!err?.toCharts || waitingForStars || !!course?.disabled;
  const sendOff = inputOff || streaming;
  // Screen readers: say once that the answer has arrived (the streamed text itself is not live).
  const [announce, setAnnounce] = useState('');
  const wasStreaming = useRef(streaming);

  // Follow new content only while the reader is at the bottom; after they send, always.
  useEffect(() => {
    const el = scroller.current;
    if (!el || !(nearBottom.current || forceScroll.current)) return;
    forceScroll.current = false;
    nearBottom.current = true;
    const behavior = streaming ? 'auto' : 'smooth';
    if (typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior });
    else el.scrollTop = el.scrollHeight;
  }, [messages, streaming, canShowChips, loadState, error, waitingForStars]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (el) nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  }, []);

  // Composer grows with its text (1–4 lines; the cap is max-height in the CSS).
  useEffect(() => {
    const el = field.current;
    if (!el || !el.scrollHeight) return;
    el.style.height = 'auto';
    // scrollHeight leaves out the borders; add them or the last 2 px of text clip.
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [input]);

  useEffect(() => {
    if (streaming) setAnnounce('');
    else if (wasStreaming.current && !error) {
      const last = messages.at(-1);
      if (last?.role === 'assistant' && last.content) setAnnounce(t('chat.answerReceived'));
    }
    wasStreaming.current = streaming;
  }, [streaming, error, messages, t]);

  // Show the top-up sheet when the chat hook signals needTopUp.
  useEffect(() => { if (needTopUp) setShowTopUp(true); }, [needTopUp]);

  function handleTopUpDismiss(): void {
    setShowTopUp(false);
    // The unanswered question goes back into the composer (before any text typed since).
    const text = dismissTopUp();
    if (text) setInput((cur) => (cur.trim() ? `${text}\n${cur}` : text));
  }

  function handleTopUpPaid(): void {
    setShowTopUp(false);
    void reloadAfterTopUp();
  }

  function handleRetry(): void {
    const pending = course ? messages.filter((m) => m.role === 'user' && m.unsent).at(-1) : undefined;
    if (pending?.content === input.trim()) setInput('');
    retry();
  }

  function submit(raw: string): void {
    const text = raw.trim();
    if (!text || sendOff) return;
    forceScroll.current = true;
    setInput('');
    send(text);
    // Keep the keyboard open (iOS closes it when the focused field loses focus).
    field.current?.focus();
  }

  /** Tapping the send button or a chip must not take focus from the composer. */
  const keepFocus = (e: PointerEvent<HTMLElement>): void => e.preventDefault();

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    // Touch devices: Enter is a newline and only the send button sends (like Telegram).
    if (isTouchDevice()) return;
    e.preventDefault();
    submit(input);
  }

  const toCharts = (): void => { void navigate(course ? '/course' : '/me/charts', { replace: true }); };
  // Opened by a startapp link: there is no previous entry to go back to.
  const back = (): void => { if (isFirstHistoryEntry()) toCharts(); else void navigate(-1); };

  const relationLabel = header
    ? header.isSelf ? t('me:charts.relation.self') : t(relationDisplayKey(header.relation, false, header.sex), { ns: 'me' })
    : '';
  const title = course?.title ?? (header?.isSelf || !header?.name
    ? relationLabel
    : t('chat.headerTitle', { name: header.name, relation: relationLabel }));

  // Which chart this conversation is about: null = the own chart, undefined = not known yet (a thread that is still loading).
  let chartOfChat: string | null | undefined;
  if (target.kind === 'new') chartOfChat = target.chartId;
  else if (loadState === 'ready') chartOfChat = header?.isSelf ? null : (chat.chartId ?? undefined);
  const chartKnown = !!course || chartOfChat !== undefined;
  const currentId = chat.conversationId ?? (target.kind === 'thread' ? target.threadId : null);
  const newConversation = (): void => {
    setShowHistory(false);
    if (course) { void navigate('/course'); return; }
    void navigate(newChatHref({ id: chartOfChat ?? '', isSelf: !chartOfChat }), { replace: true });
  };
  const openConversation = (thread: ThreadItem): void => {
    setShowHistory(false);
    void navigate(historyThreadHref(thread), { replace: true });
  };

  const chips = chipKeys(header?.relation ?? null, header?.isSelf ?? true, header?.sex ?? null);

  return (
    <div className={styles.root} data-testid={course ? "course-chat" : "astrologer-chat"}>
      <CosmosBackdrop />
      <div className={styles.column}>
        <div className={`${headerStyles.topBar} ${styles.header}`}>
          <button
            type="button"
            className={`${headerStyles.backBtn} ${styles.back}`}
            data-testid="chat-back"
            onClick={back}
            aria-label={t('chat.back')}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <h1 className={`${styles.title} ${styles.titleSingleLine}`} data-testid="chat-title" title={title}>
            {course ? <span className={styles.personaName}>{title}</span> : (
            <button
              type="button"
              className={styles.personaBtn}
              onClick={() => setShowPersona(true)}
              disabled={streaming || waitingForStars}
              aria-label={t('chat.persona.open', { name: title })}
              aria-haspopup="dialog"
              data-testid="chat-persona"
            >
              <span className={styles.personaName}>{title}</span>
            </button>
            )}
          </h1>
          <div className={styles.gauge}>
            <StarGauge compact onTopUp={() => setShowTopUp(true)} />
          </div>
          <ChatHeaderActions
            ref={historyButton}
            onHistory={() => setShowHistory(true)}
            onNew={newConversation}
            disabled={streaming || waitingForStars || !chartKnown}
          />
        </div>

        {course?.notice}

        {loadState === 'loading' && (
          <div className={styles.center} data-testid="chat-loading">
            <OrbitalLoader sigilSize={120} />
          </div>
        )}

        {loadState === 'error' && (
          <div className={styles.center}>
            <p ref={errorRef} tabIndex={-1} className={styles.centerText} role="alert" data-testid="chat-load-error">
              {course ? t(course.loadCode === 'course_locked' ? 'course:chat.locked' : loadError === 'not_found' ? 'course:chat.gone' : 'course:chat.error') : loadError === 'chart_not_found' || loadError === 'not_found'
                ? t('chat.load.notFound')
                : loadError === 'chart_not_ready'
                  ? t('chat.load.notReady')
                  : t('chat.load.network')}
            </p>
            <div className={styles.centerActions}>
              {(loadError === 'chart_not_found' || loadError === 'not_found') && (
                <PrimaryButton data-testid="chat-to-charts" onClick={toCharts}>
                  {t(course ? 'course:lesson.toCourse' : 'chat.toCharts')}
                </PrimaryButton>
              )}
              {loadError === 'chart_not_ready' && (
                <SecondaryButton data-testid="chat-load-back" onClick={() => navigate(-1)}>
                  {t('chat.back')}
                </SecondaryButton>
              )}
              {loadError !== 'chart_not_found' && loadError !== 'not_found' && loadError !== 'chart_not_ready' && (
                <PrimaryButton data-testid="chat-load-retry" onClick={reload}>
                  {t('chat.retry')}
                </PrimaryButton>
              )}
            </div>
          </div>
        )}

        {loadState === 'ready' && (
          <div ref={scroller} className={styles.scroll} data-testid="chat-messages" onScroll={onScroll}>
            {messages.map((m, i) => (
              <Fragment key={i}>
              <div
                className={`${styles.bubble} ${m.role === 'user' ? styles.user : styles.assistant}`}
                data-testid={`chat-msg-${i}`}
              >
                {m.role === 'user' || (course && i === 0) ? (
                  m.content
                ) : m.content ? (
                  <ReportMarkdown
                    // while the answer is still arriving, an unfinished **bold** run must not show raw asterisks
                    markdown={streaming && i === messages.length - 1 ? closePartialBold(m.content) : m.content}
                    fallback={<span className={styles.plain}>{m.content}</span>}
                  />
                ) : (
                  <span className={styles.typing} role="status" aria-label={t('chat.thinking')} data-testid="chat-typing">
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                    <span className={styles.dot} />
                  </span>
                )}
              </div>
              {course && i === 0 && !!course.quickReplies?.length && (
                <div className={styles.chips} data-testid="course-chat-chips">
                  {course.quickReplies.map((text, index) => (
                    <button
                      key={text}
                      type="button"
                      className={styles.chip}
                      data-testid={`course-chat-chip-${index + 1}`}
                      disabled={sendOff}
                      onPointerDown={keepFocus}
                      onClick={() => { forceScroll.current = true; send(text); field.current?.focus(); }}
                    >
                      {text}
                    </button>
                  ))}
                </div>
              )}
              </Fragment>
            ))}
            {canShowChips && (
              <div className={styles.chips} data-testid="chat-chips">
                {chips.map((key, i) => (
                  <button
                    key={key}
                    type="button"
                    className={styles.chip}
                    data-testid={`chat-chip-${i + 1}`}
                    onPointerDown={keepFocus}
                    onClick={() => { forceScroll.current = true; send(t(key)); field.current?.focus(); }}
                    disabled={sendOff}
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {err && (
          <p ref={errorRef} tabIndex={-1} role="alert" data-testid="chat-error" className={styles.error}>
            <span>{t(course ? (error === 'quote_changed' || error === 'answer_changed' || error === 'version_conflict' ? 'course:chat.changed' : error === 'chat_disabled' ? 'course:chat.disabled' : error === 'course_locked' ? 'course:chat.locked' : err.toCharts ? 'course:chat.gone' : err.key) : err.key)}</span>
            {err.retry && (
              <button type="button" className={styles.action} data-testid="chat-retry" onClick={handleRetry} disabled={streaming}>
                {t('chat.retry')}
              </button>
            )}
            {err.toCharts && (
              <button type="button" className={styles.action} data-testid="chat-to-charts" onClick={toCharts}>
                {t(course ? 'course:lesson.toCourse' : 'chat.toCharts')}
              </button>
            )}
          </p>
        )}

        <p className={styles.srOnly} aria-live="polite" data-testid="chat-announce">{announce}</p>

        {waitingForStars && (
          <p role="status" data-testid="chat-waiting-stars" className={styles.waiting}>
            {t('chat.waitingStars')}
          </p>
        )}

        {loadState === 'ready' && (
          <form
            className={styles.composer}
            data-has-text={input.trim() ? 'true' : 'false'}
            onSubmit={(e) => { e.preventDefault(); submit(input); }}
          >
            <textarea
              ref={field}
              className={styles.input}
              rows={1}
              value={input}
              disabled={inputOff}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              maxLength={MAX_CHARS}
              placeholder={t(course ? 'course:chat.placeholder' : 'chat.placeholder')}
              aria-label={t(course ? 'course:chat.placeholder' : 'chat.placeholder')}
              data-testid="chat-input"
            />
            <button
              type="submit"
              className={styles.send}
              data-testid="chat-send"
              aria-label={t('chat.send')}
              onPointerDown={keepFocus}
              disabled={sendOff || !input.trim()}
            >
              <span aria-hidden="true">↑</span>
            </button>
          </form>
        )}
      </div>

      {showHistory && chartKnown && (
        <ChatHistorySheet
          currentId={currentId}
          onClose={() => setShowHistory(false)}
          onOpen={openConversation}
          onDeleted={(id) => { if (id === currentId) newConversation(); }}
        />
      )}
      {showPersona && !course && (
        <ChatPersonaPicker
          currentChartId={chartOfChat}
          onClose={() => setShowPersona(false)}
          onPick={(href) => { setShowPersona(false); void navigate(href, { replace: true }); }}
        />
      )}
      {showTopUp && <ChatTopUpSheet priceStars={course?.priceStars} onDismiss={handleTopUpDismiss} onPaid={handleTopUpPaid} />}
    </div>
  );
}
